/**
 * The Audit screen (§A PR-3) — every recorded action in this account, on
 * route P25 (`GET /audit-logs`).
 *
 * Filters — `action`, `outcome`, `actorRef`, `from`, `to` — live in the URL,
 * exactly the parameters the route's schema validates. That makes the Users
 * screen's "View audit" pivot a plain link (`/audit?actorRef=usr_…`, using
 * `AuditLogDto.actorRef`), and it makes "the denied requests from Tuesday"
 * something that survives being pasted into a support thread. Text filters
 * commit on form submit, not per keystroke; a changed filter drops `page`.
 *
 * **Metadata is rendered verbatim.** Identifiers in audit metadata arrive
 * masked from the server where masking applies — that is a server-side
 * concern, and this screen must not transform, "fix" or unmask what it shows.
 * A masked `9198xxxxxx12` renders byte-identical to the wire.
 *
 * The DTO has no id, so the row key is `${requestId}:${createdAt}:${action}`
 * — `requestId` alone is not unique (one request may write several audit
 * rows: a grant plus the action it granted), but the same action twice within
 * one request at one timestamp is, by the recorder's construction, one row.
 */

import type { AuditLogDto, AuditOutcome } from '@rd/shared';
import { useCallback, useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router';

import { Badge, type BadgeTone } from '../../components/Badge.js';
import { Button } from '../../components/Button.js';
import { EmptyState } from '../../components/EmptyState.js';
import { ErrorState } from '../../components/ErrorState.js';
import { Field } from '../../components/Field.js';
import { Input, Select } from '../../components/Input.js';
import { Modal } from '../../components/Modal.js';
import { PageHeader } from '../../components/PageHeader.js';
import { Pagination } from '../../components/Pagination.js';
import { Skeleton } from '../../components/Skeleton.js';
import { Table, type Column } from '../../components/Table.js';
import { downloadCsv, toCsv, type CsvColumn } from '../../lib/csv.js';
import { usePaginatedQuery } from '../../lib/paginated.js';

/** The outcome enum from the route's own schema — ours, so transcription. */
const OUTCOME_FILTERS: readonly AuditOutcome[] = ['SUCCESS', 'DENIED', 'FAILED'];

/**
 * Outcome → tone, total over the union so a fourth outcome is a compile
 * error. The Badge always carries the word itself — colour is never the only
 * channel (WCAG 1.4.1), and DENIED-vs-FAILED is a distinction an auditor
 * reads, not one they should have to judge by hue.
 */
const OUTCOME_TONE: Record<AuditOutcome, BadgeTone> = {
  SUCCESS: 'success',
  DENIED: 'warning',
  FAILED: 'error',
};

function formatWhen(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? iso : at.toLocaleString();
}

export function AuditLogPage(): ReactElement {
  const [params, setParams] = useSearchParams();

  const action = params.get('action') ?? '';
  const outcome = params.get('outcome') ?? '';
  const actorRef = params.get('actorRef') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';

  // Drafts for the two free-text filters; only Apply writes them to the URL.
  const [actionDraft, setActionDraft] = useState(action);
  const [actorDraft, setActorDraft] = useState(actorRef);

  const setFilters = useCallback(
    (updates: Record<string, string>) => {
      const next = new URLSearchParams(params);
      for (const [key, value] of Object.entries(updates)) {
        if (value === '') next.delete(key);
        else next.set(key, value);
      }
      // A new filter is a new result set; page 4 of the old one is a trap.
      next.delete('page');
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const query = usePaginatedQuery<AuditLogDto>({
    key: ['audit-logs'],
    path: '/audit-logs',
    // Only what the user set — `outcome=` would be a validation failure, not
    // "any outcome" (the JobListPage rule).
    query: {
      ...(action === '' ? {} : { action }),
      ...(outcome === '' ? {} : { outcome }),
      ...(actorRef === '' ? {} : { actorRef }),
      ...(from === '' ? {} : { from }),
      ...(to === '' ? {} : { to }),
    },
  });

  const [selected, setSelected] = useState<AuditLogDto | null>(null);

  const columns: Column<AuditLogDto>[] = [
    {
      key: 'createdAt',
      header: 'When',
      render: (row) => <span className="text-muted">{formatWhen(row.createdAt)}</span>,
    },
    {
      // Actions are dotted machine names (`users.force_logout`); monospace for
      // the same reason identifiers are — they get compared, not read.
      key: 'action',
      header: 'Action',
      kind: 'mono',
      render: (row) => <span className="text-sm">{row.action}</span>,
    },
    {
      key: 'outcome',
      header: 'Outcome',
      kind: 'status',
      render: (row) => (
        <Badge size="sm" tone={OUTCOME_TONE[row.outcome]}>
          {row.outcome}
        </Badge>
      ),
    },
    {
      key: 'actor',
      header: 'Who',
      render: (row) => (
        // System rows have no email; the scope is the honest answer.
        <span className="text-body">{row.actorEmail ?? row.actorScope}</span>
      ),
    },
    {
      key: 'target',
      header: 'Target',
      render: (row) => {
        if (row.targetType === null && row.targetRef === null && row.targetCount === null) {
          return <span className="text-muted">—</span>;
        }
        return (
          <span className="text-muted">
            {row.targetType ?? ''}
            {row.targetRef ? (
              <span className="ml-1 font-[family-name:var(--font-mono)] text-body">
                {row.targetRef}
              </span>
            ) : null}
            {row.targetCount !== null ? ` × ${row.targetCount}` : ''}
          </span>
        );
      },
    },
    {
      key: 'errorCode',
      header: 'Error',
      kind: 'mono',
      render: (row) => <span className="text-sm">{row.errorCode ?? '—'}</span>,
    },
    {
      key: 'requestId',
      header: 'Request',
      kind: 'mono',
      render: (row) => <span className="text-sm">{row.requestId}</span>,
    },
    {
      key: 'details',
      header: 'Details',
      kind: 'actions',
      render: (row) => (
        // A button, not a clickable `<tr>`: `Table` owns its rows, and a row
        // onClick would be invisible to a keyboard anyway. This is the
        // "row click → metadata" affordance, in reachable form.
        <Button variant="ghost" size="sm" onClick={() => setSelected(row)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Audit log"
        description="Every recorded action in this account: who did what, to what, and how it went."
        actions={
          // §A PR-6: built from the server-returned rows on screen. Metadata
          // is deliberately not flattened into the CSV — it is free-form JSON
          // the modal renders verbatim, and a spreadsheet cell is not that.
          <Button
            variant="secondary"
            disabled={(query.data?.data.length ?? 0) === 0}
            onClick={() => {
              const rows = query.data?.data ?? [];
              const columns: CsvColumn<AuditLogDto>[] = [
                { header: 'createdAt', value: (r) => r.createdAt },
                { header: 'action', value: (r) => r.action },
                { header: 'outcome', value: (r) => r.outcome },
                { header: 'actorScope', value: (r) => r.actorScope },
                { header: 'actorEmail', value: (r) => r.actorEmail },
                { header: 'actorRef', value: (r) => r.actorRef },
                { header: 'targetType', value: (r) => r.targetType },
                { header: 'targetRef', value: (r) => r.targetRef },
                { header: 'targetCount', value: (r) => r.targetCount },
                { header: 'errorCode', value: (r) => r.errorCode },
                { header: 'requestId', value: (r) => r.requestId },
              ];
              downloadCsv(`audit-page-${query.page}.csv`, toCsv(columns, rows));
            }}
          >
            Download CSV (this page)
          </Button>
        }
      />

      <form
        aria-label="Filters"
        className="mb-6 grid items-end gap-4 sm:grid-cols-2 lg:grid-cols-6"
        onSubmit={(event) => {
          event.preventDefault();
          setFilters({ action: actionDraft.trim(), actorRef: actorDraft.trim() });
        }}
      >
        <Field label="Action" hint="e.g. users.create">
          {(field) => (
            <Input
              id={field.id}
              value={actionDraft}
              className="font-[family-name:var(--font-mono)]"
              onChange={(event) => setActionDraft(event.target.value)}
            />
          )}
        </Field>

        <Field label="Outcome">
          {(field) => (
            <Select
              id={field.id}
              value={outcome}
              onChange={(event) => setFilters({ outcome: event.target.value })}
            >
              <option value="">Any outcome</option>
              {OUTCOME_FILTERS.map((key) => (
                <option key={key} value={key}>
                  {key}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="Actor" hint="A usr_… reference">
          {(field) => (
            <Input
              id={field.id}
              value={actorDraft}
              className="font-[family-name:var(--font-mono)]"
              onChange={(event) => setActorDraft(event.target.value)}
            />
          )}
        </Field>

        <Field label="From">
          {(field) => (
            <Input
              id={field.id}
              type="date"
              value={from}
              onChange={(event) => setFilters({ from: event.target.value })}
            />
          )}
        </Field>

        <Field label="To">
          {(field) => (
            <Input
              id={field.id}
              type="date"
              value={to}
              onChange={(event) => setFilters({ to: event.target.value })}
            />
          )}
        </Field>

        <Button type="submit" variant="secondary">
          Apply
        </Button>
      </form>

      {query.isPending ? (
        <div className="flex flex-col gap-2">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : null}

      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : null}

      {query.data ? (
        <>
          <Table
            caption="Audit entries, newest first"
            columns={columns}
            rows={query.data.data}
            // See the header: the DTO has no id, and requestId alone repeats
            // when one request writes several rows.
            rowKey={(row) => `${row.requestId}:${row.createdAt}:${row.action}`}
            empty={
              <EmptyState
                title="Nothing recorded"
                body="No audit entries match these filters."
              />
            }
          />
          <Pagination meta={query.data.meta} onPageChange={query.setPage} label="Audit entries" />
        </>
      ) : null}

      <Modal
        open={selected !== null}
        title={selected === null ? 'Audit entry' : `${selected.action} — ${selected.outcome}`}
        onClose={() => setSelected(null)}
      >
        {selected === null ? null : (
          <div className="flex flex-col gap-[var(--space-3)]">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[length:var(--text-sm)]">
              <dt className="text-muted">When</dt>
              <dd>{formatWhen(selected.createdAt)}</dd>
              <dt className="text-muted">Who</dt>
              <dd>{selected.actorEmail ?? selected.actorScope}</dd>
              {selected.actorRef !== null ? (
                <>
                  <dt className="text-muted">Actor ref</dt>
                  <dd className="font-[family-name:var(--font-mono)]">{selected.actorRef}</dd>
                </>
              ) : null}
              {selected.targetRef !== null ? (
                <>
                  <dt className="text-muted">Target</dt>
                  <dd className="font-[family-name:var(--font-mono)]">
                    {selected.targetType} {selected.targetRef}
                  </dd>
                </>
              ) : null}
              {selected.errorCode !== null ? (
                <>
                  <dt className="text-muted">Error</dt>
                  <dd className="font-[family-name:var(--font-mono)]">{selected.errorCode}</dd>
                </>
              ) : null}
              <dt className="text-muted">Request</dt>
              <dd className="font-[family-name:var(--font-mono)]">{selected.requestId}</dd>
            </dl>

            {selected.metadata !== null ? (
              /*
               * Pretty-printed, but otherwise VERBATIM. Masked identifiers
               * (`9198xxxxxx12`) must render exactly as the server sent them —
               * masking is the server's decision and this screen neither
               * unmasks nor re-masks (§A, the masking contract).
               */
              <pre className="overflow-x-auto rounded-[var(--radius-md)] border border-line bg-surface-alt p-3 font-[family-name:var(--font-mono)] text-[length:var(--text-xs)] text-body">
                {JSON.stringify(selected.metadata, null, 2)}
              </pre>
            ) : (
              <p className="text-[length:var(--text-sm)] text-muted">
                No additional detail was recorded for this entry.
              </p>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
